import type { ContextValues, DeepReadonly } from "@atoms/atom-types";
import isEqual from "lodash/isEqual";
import {
  useContext,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { AtomStoreContext } from "../AtomStoreProvider";
import { deepFreeze } from "../use-find-atom/shared";
import { useCombinedContext } from "../useCombinedContext";
import type { SelectorFunction, UseAtomOptions } from "./makeUseAtom";
import {
  createCheckForUpdatesCallback,
  getSelectedStateFromStore,
  parseHookArguments,
  toError,
  updateErrorIfChanged,
} from "./shared";

/**
 * Result object returned by useAtomNext hook.
 * Inspired by SWR's API design for consistent loading and error states.
 *
 * @template TData - The type of data stored in the atom
 */
export interface UseAtomResult<TData> {
  /**
   * The atom data (deep frozen, readonly).
   * - `undefined` while loading (before first value received) and when the
   *   subscription failed (see `error`)
   * - `null` if atom does not exist
   * - `DeepReadonly<TData>` when successfully loaded
   */
  data: DeepReadonly<TData> | null | undefined;

  /**
   * Loading state indicator.
   * `true` from hook initialization until first value or error is received.
   */
  isLoading: boolean;

  /**
   * Error that occurred during atom fetching or processing.
   * Includes context errors, selector errors and a failed subscription: the
   * server rejected it (a `GraphQLRequestError` with class, code and
   * reference; render it with `useErrorDisplay`, never by its `message`), or
   * the connection was lost before a first value arrived. Cleared by the
   * next value.
   */
  error: Error | undefined;
}

/**
 * React refs used by the update callback for useAtomNext.
 * Extends base refs with loading state tracking.
 * @internal
 */
interface UseAtomNextCallbackRefs<TSelected, RType extends object> {
  hasReceivedValue: React.RefObject<boolean>;
  lastErrorMessage: React.RefObject<string | undefined>;
  latestSelectedState: React.RefObject<
    DeepReadonly<TSelected> | null | undefined
  >;
  latestSelector: React.RefObject<((arg0: RType) => TSelected) | undefined>;
  latestSubscriptionCallbackError: React.RefObject<Error | undefined>;
}

/**
 * Creates a callback function for handling atom subscription updates in useAtomNext.
 * This is a specialized version that manages loading state.
 *
 * @template RType - The raw atom data type
 * @template TSelected - The selected/transformed data type
 * @param refs - React refs for maintaining state across renders
 * @param equalityFn - Function to compare values for equality
 * @param setIsLoading - State setter for loading
 * @param setError - State setter for error
 * @param forceRender - Function to trigger re-render
 * @returns Callback function for atom subscription updates
 * @internal
 */
function createUseAtomNextCallback<RType extends object, TSelected>(
  refs: UseAtomNextCallbackRefs<TSelected, RType>,
  equalityFn: (a: unknown, b: unknown) => boolean,
  setIsLoading: (loading: boolean) => void,
  setError: (error: Error | undefined) => void,
  forceRender: () => void,
): (currentValue: RType | null) => void {
  return createCheckForUpdatesCallback<RType, TSelected>(refs, {
    equalityFn,
    forceRender,
    onSuccess: () => {
      if (!refs.hasReceivedValue.current) {
        refs.hasReceivedValue.current = true;
        setIsLoading(false);
      }
      // Clear previous (transient) errors once we successfully read from the store.
      // Important: don't clear errors in a layout-effect "cleanup" step, since that can
      // ping-pong with transient subscribe/context errors during route transitions.
      refs.lastErrorMessage.current = undefined;
      setError(undefined);
    },
    onError: (error: Error) => {
      setError(error);
      setIsLoading(false);
    },
  });
}

/**
 * Factory function that creates a typed useAtomNext hook.
 * Returns a hook with proper TypeScript types for the atom system.
 *
 * @template AtomType - String union type of all atom type identifiers
 * @template AtomDataFromAtomType - Mapping from atom types to their data interfaces
 * @returns A typed useAtomNext hook function
 *
 * @example
 * ```typescript
 * const useAtomNext = makeUseAtomNext<Atoms, AtomDataIndex>();
 * ```
 */
export function makeUseAtomNext<
  AtomType extends string,
  AtomDataFromAtomType,
>() {
  /**
   * React hook for subscribing to atom data with SWR-like API.
   * Returns an object with data, loading state, and error instead of throwing.
   *
   * Unlike useAtom, this hook:
   * - Never throws errors (returns them in error field)
   * - Provides explicit isLoading state
   * - Returns null for non-existent atoms instead of throwing
   *
   * @param type - The atom type to subscribe to
   * @param options - Optional configuration
   * @returns Object with data, isLoading, and error properties
   *
   * @example
   * ```typescript
   * const { data: product, isLoading, error } = useAtomNext(Atoms.Product);
   *
   * if (error) return <Error error={error} />;
   * if (isLoading) return <Loading />;
   * if (!product) return <NotFound />;
   * return <ProductView product={product} />;
   * ```
   */
  function useAtomNext<
    TType extends keyof AtomDataFromAtomType,
    IType extends AtomDataFromAtomType[TType],
  >(type: TType, options?: UseAtomOptions): UseAtomResult<IType>;

  /**
   * React hook for subscribing to atom data with selector and SWR-like API.
   * The selector function transforms the atom data before returning.
   *
   * @param type - The atom type to subscribe to
   * @param selector - Function to transform/select data from atom
   * @param options - Optional configuration
   * @returns Object with selected data, isLoading, and error properties
   *
   * @example
   * ```typescript
   * const { data: productName, isLoading } = useAtomNext(
   *   Atoms.Product,
   *   (product) => product.name
   * );
   * ```
   */
  function useAtomNext<
    TType extends keyof AtomDataFromAtomType,
    IType extends AtomDataFromAtomType[TType],
    TSelected,
  >(
    type: TType,
    selector: SelectorFunction<IType, TSelected>,
    options?: UseAtomOptions,
  ): UseAtomResult<TSelected>;

  /**
   * React hook for subscribing to atom data with selector (generic version).
   *
   * @param type - The atom type identifier
   * @param selector - Function to transform/select data from atom
   * @param options - Optional configuration
   * @returns Object with selected data, isLoading, and error properties
   */
  function useAtomNext<RType extends object, TSelected = RType>(
    type: AtomType,
    selector: SelectorFunction<RType, TSelected>,
    options?: UseAtomOptions,
  ): UseAtomResult<TSelected>;

  /**
   * React hook for subscribing to atom data (generic version).
   *
   * @param type - The atom type identifier
   * @param options - Optional configuration
   * @returns Object with data, isLoading, and error properties
   */
  function useAtomNext<RType extends object, TSelected = RType>(
    type: AtomType,
    options?: UseAtomOptions,
  ): UseAtomResult<TSelected>;

  function useAtomNext<RType extends object, TSelected = RType>(
    type: AtomType,
    selectorOrOptions?: SelectorFunction<RType, TSelected> | UseAtomOptions,
    options?: UseAtomOptions,
  ): UseAtomResult<TSelected> {
    const equalityFn = isEqual;

    const { selector, extraContext, shouldSkip, getAtomParameters } =
      parseHookArguments(selectorOrOptions, options);

    const atomStore = useContext(AtomStoreContext);
    const combinedContext = useCombinedContext();
    const context: ContextValues<string> = useMemo(
      () => ({
        ...combinedContext,
        ...extraContext,
      }),
      [combinedContext, extraContext],
    );

    const latestSelector = useRef<(arg0: RType) => TSelected>(undefined);
    const latestSelectedState = useRef<
      DeepReadonly<TSelected> | null | undefined
    >(undefined);
    const [, forceRender] = useReducer((s) => s + 1, 0);
    let selectedState: DeepReadonly<TSelected> | null | undefined;

    const latestSubscriptionCallbackError = useRef<Error | undefined>(
      undefined,
    );
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | undefined>(undefined);
    const prevSelectedState = useRef<
      DeepReadonly<TSelected> | null | undefined
    >(undefined);
    const hasReceivedValue = useRef(false);
    const atomIdRef = useRef<string | null>(null);
    const lastErrorMessage = useRef<string | undefined>(undefined);
    // Store the data returned to the component (frozen, not safe to mutate)
    const returnedData = useRef<DeepReadonly<TSelected> | null | undefined>(
      undefined,
    );

    // Try to compute atomId - if it fails, we have an error
    // Use useMemo to stabilize error object
    const { atomId, contextError } = useMemo(() => {
      try {
        if (!shouldSkip && atomStore) {
          const id = atomStore.pathForAtomWithContext(
            type,
            context,
            getAtomParameters,
          );
          return { atomId: id, contextError: undefined };
        }
        return { atomId: null, contextError: undefined };
      } catch (err) {
        return { atomId: null, contextError: toError(err) };
      }
    }, [shouldSkip, atomStore, type, context, getAtomParameters]);

    if (
      selector !== latestSelector.current ||
      latestSubscriptionCallbackError.current
    ) {
      const result = getSelectedStateFromStore({
        atomStore,
        type,
        context,
        getAtomParameters,
        selector,
        shouldSkip,
      });

      const frozenNewState = result.value && deepFreeze(result.value);
      if (!equalityFn(frozenNewState, prevSelectedState.current)) {
        selectedState = frozenNewState;
      } else {
        selectedState = prevSelectedState.current;
      }
    } else {
      selectedState = latestSelectedState.current;
    }

    useLayoutEffect(() => {
      latestSelector.current = selector;
      latestSelectedState.current = selectedState;
      prevSelectedState.current = selectedState;
      latestSubscriptionCallbackError.current = undefined;
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: equalityFn belongs in deps so the subscription picks up comparer changes; Biome incorrectly flags it as unnecessary
    useLayoutEffect(() => {
      // Handle context errors
      if (contextError) {
        if (updateErrorIfChanged(contextError, lastErrorMessage, setError)) {
          setIsLoading(false);
        }
        return;
      }

      if (shouldSkip) {
        setIsLoading(false);
        return;
      }

      // Only reset if atomId changed
      if (atomId !== atomIdRef.current) {
        atomIdRef.current = atomId;
        hasReceivedValue.current = false;
        lastErrorMessage.current = undefined;
        setIsLoading(true);
        setError(undefined);
      }

      const checkForUpdates = createUseAtomNextCallback(
        {
          hasReceivedValue,
          lastErrorMessage,
          latestSelectedState,
          latestSelector,
          latestSubscriptionCallbackError,
        },
        equalityFn,
        setIsLoading,
        setError,
        forceRender,
      );

      // The subscription failed: the same state a hook gets that mounts
      // after the failure.
      const failWith = (subscriptionError: Error) => {
        latestSelectedState.current = undefined;
        lastErrorMessage.current = subscriptionError.message;
        setError(subscriptionError);
        setIsLoading(false);
      };

      try {
        atomStore?.subscribe(
          type,
          context,
          checkForUpdates,
          selector,
          getAtomParameters,
          failWith,
        );
        const currentStoreValue = atomStore?.getAtom(
          type,
          context,
          getAtomParameters,
        );
        // Only treat a *definitive* value (null = does not exist, object = exists)
        // as an update. `undefined` means "not yet received" and must not flip
        // `isLoading` to false, otherwise UIs can flash NotFound states.
        if (typeof currentStoreValue !== "undefined") {
          checkForUpdates(currentStoreValue);
        }
      } catch (err) {
        // Also the error of a subscription that had failed before this hook
        // asked for it: no value of an atom it showed before stays next to it.
        latestSelectedState.current = undefined;
        if (updateErrorIfChanged(toError(err), lastErrorMessage, setError)) {
          setIsLoading(false);
        }
      }

      return () => {
        try {
          atomStore?.unsubscribe(
            type,
            context,
            checkForUpdates,
            getAtomParameters,
          );
        } catch {
          // Silently catch unsubscribe errors to prevent cleanup errors
        }
      };
    }, [
      atomId,
      atomStore,
      selector,
      contextError,
      shouldSkip,
      type,
      context,
      getAtomParameters,
      equalityFn,
    ]);

    // Only update if the value has actually changed (by deep equality)
    // This maintains referential stability while deepFreeze prevents mutations from affecting internal state
    if (!equalityFn(selectedState, returnedData.current)) {
      returnedData.current = selectedState;
    }

    return {
      data: returnedData.current,
      isLoading,
      error,
    };
  }

  return useAtomNext;
}

/**
 * Default untyped instance of useAtomNext for internal use.
 * Projects should create their own typed instance using makeUseAtomNext.
 * @internal
 */
export const __useAtomNext = makeUseAtomNext<string, unknown>();
